"use client"

import * as React from "react"
import { Building2Icon } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/common/elements/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/common/elements/sidebar"
import { ChevronsUpDownIcon } from "lucide-react"

type EntityOption = {
  id: number
  name: string
  logo: React.ElementType
}

export function EntitySelector({
  entities,
  isDisabled = false,
  fallbackLabel = "No entities available",
  title = "Entities",
  selectedEntityId,
  onEntityChange,
  onEntitySelected,
}: {
  entities: EntityOption[]
  isDisabled?: boolean
  fallbackLabel?: string
  title?: string
  selectedEntityId?: number | null
  onEntityChange?: (entity: EntityOption) => void
  onEntitySelected?: (entity: EntityOption) => void
}) {
  const { isMobile } = useSidebar()

  const activeEntity = React.useMemo(() => {
    if (entities.length === 0) {
      return null
    }

    if (selectedEntityId !== undefined && selectedEntityId !== null) {
      const selectedEntity = entities.find((entity) => entity.id === selectedEntityId)
      if (selectedEntity) {
        return selectedEntity
      }
    }

    return entities[0]
  }, [entities, selectedEntityId])

  const handleEntitySelected = React.useCallback(
    (entity: EntityOption) => {
      onEntityChange?.(entity)
      onEntitySelected?.(entity)
    },
    [onEntityChange, onEntitySelected]
  )

  const isSelectorDisabled = isDisabled || entities.length === 0
  const shouldRenderStaticEntity = entities.length === 1 && activeEntity !== null

  const displayEntity = activeEntity ?? {
    name: fallbackLabel,
    logo: Building2Icon,
    plan: "Unavailable",
  }

  if (shouldRenderStaticEntity) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="cursor-default">
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <displayEntity.logo className="size-4" />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{displayEntity.name}</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              disabled={isSelectorDisabled}
              aria-disabled={isSelectorDisabled}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <displayEntity.logo className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{displayEntity.name}</span>
              </div>
              <ChevronsUpDownIcon className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {title}
            </DropdownMenuLabel>
            {entities.map((entity) => (
              <DropdownMenuItem
                key={entity.id}
                onClick={() => handleEntitySelected(entity)}
                className="gap-2 p-2"
              >
                <div className="flex size-6 items-center justify-center rounded-md border">
                  <entity.logo className="size-4" />
                </div>
                {entity.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
